import { Tooltip } from '@mantine/core'
import { Users, Zap } from 'lucide-react'

import IconEdit from 'src/icons/IconEdit'
import IconSparkle from 'src/icons/IconSparkle'

export type ActorBadgeVariant = 'auto' | 'bulk' | 'ai' | 'manual'

const PILL_BASE =
  'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-medium leading-none'

const VARIANTS: Record<
  ActorBadgeVariant,
  {
    icon: React.ReactNode
    text: string
    className: string
  }
> = {
  // Automated rule / system action (texts only today).
  auto: {
    icon: <Zap className="h-2.5 w-2.5" />,
    text: 'Auto',
    className: `${PILL_BASE} bg-doubleNickel-brand-50 text-doubleNickel-brand-700`,
  },
  // Part of a bulk SMS / call action.
  bulk: {
    icon: <Users className="h-2.5 w-2.5" />,
    text: 'Bulk',
    className: `${PILL_BASE} bg-doubleNickel-brand-50 text-doubleNickel-brand-700`,
  },
  // AI voice agent (BlandAI). Shares the brand pill color with auto/bulk; the
  // sparkle icon (reserved exclusively for AI) is what sets it apart.
  ai: {
    icon: <IconSparkle size={10} />,
    text: 'AI',
    className: `${PILL_BASE} bg-doubleNickel-brand-50 text-doubleNickel-brand-700`,
  },
  // Hand-logged by a recruiter.
  manual: {
    icon: (
      <IconEdit className="h-2.5 w-2.5" fill="none" stroke="currentColor" />
    ),
    text: 'Manual',
    className: `${PILL_BASE} bg-doubleNickel-gray-100 text-doubleNickel-gray-600`,
  },
}

/**
 * Shared actor marker pill for activity-log bubbles. Centralizes the icon,
 * label, and color for each actor type so SMS and Call threads stay
 * consistent; the tooltip wording is passed in per context.
 */
const ActorBadge = ({
  variant,
  label,
  className: extraClassName,
}: {
  variant: ActorBadgeVariant
  label: string
  className?: string
}) => {
  const { icon, text, className } = VARIANTS[variant]
  return (
    <Tooltip label={label} position="top">
      <span
        className={
          extraClassName ? `${className} ${extraClassName}` : className
        }
      >
        {icon}
        {text}
      </span>
    </Tooltip>
  )
}

export default ActorBadge
